import React, { useState, useEffect, useContext } from 'react';
import { Container, Table, Card, Spinner } from 'react-bootstrap';
import Header from './Header';
import { ProductContext } from '../context/AppContext';

const FeeAccuracyReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { GetstudentData } = useContext(ProductContext);
  
  const studentId = GetstudentData?.id;
  const SessionId = GetstudentData?.session;
  const studentFeeID = GetstudentData?.fee_cycle_id;

  const monthOrder = [
    { num: 4, label: 'APR' },
    { num: 5, label: 'MAY' },
    { num: 6, label: 'JUN' },
    { num: 7, label: 'JUL' },
    { num: 8, label: 'AUG' },
    { num: 9, label: 'SEP' },
    { num: 10, label: 'OCT' },
    { num: 11, label: 'NOV' },
    { num: 12, label: 'DEC' },
    { num: 1, label: 'JAN' },
    { num: 2, label: 'FEB' },
    { num: 3, label: 'MAR' }
  ];

  const fetchFeeAccuracy = async () => {
    setLoading(true);
    setError(null);
    try {
      const authtoken = localStorage.getItem("token");
      const response = await fetch('https://api.mirai.healthcare/api/fee-service/fee-analytics/fee-category-accuracy', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'x-auth': authtoken
        },
        body: JSON.stringify({
          session_id: SessionId,
          fee_percentage: 100,
          fee_type: studentFeeID,
          student_id: studentId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP Error Status: ${response.status}`);
      }

      const data = await response.json();

      if (data && (data.success === true || data.success === "true")) {
        setReportData(data);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId && SessionId) {
      fetchFeeAccuracy();
    } else {
      setLoading(false);
    }
  }, [studentId, SessionId]);

  const processTableData = () => {
    if (!reportData || !reportData.months) return null;

    const categoriesSet = new Set();
    reportData.months.forEach(m => {
      m.fee_categories?.forEach(cat => categoriesSet.add(cat.category_name));
    });
    const categoryList = Array.from(categoriesSet);

    const categoryMatrix = {};
    categoryList.forEach(cat => {
      categoryMatrix[cat] = {};
    });

    const monthAllFees = {};

    reportData.months.forEach(m => {
      let monthSum = 0;
      let monthCount = 0;

      m.fee_categories?.forEach(cat => {
        categoryMatrix[cat.category_name][m.month] = cat.accuracy_percentage;
        monthSum += cat.accuracy_percentage;
        monthCount++;
      });

      monthAllFees[m.month] = monthCount > 0 ? Math.round(monthSum / monthCount) : 0;
    });

    return { categoryList, categoryMatrix, monthAllFees };
  };

  const processed = processTableData();
  const student_details = reportData?.student_details || {};

  const allFeesValues = processed ? monthOrder.map(m => processed.monthAllFees[m.num] ?? 0) : [];
  const overallAllFeesAvg = allFeesValues.length > 0 
    ? (allFeesValues.reduce((a, b) => a + b, 0) / allFeesValues.length).toFixed(2)
    : "0.00";

  return (
    <div className="page-wrapper">
      <div className="content">
        <Header />
        <Container fluid className="py-4">
          {loading ? (
            /* 1. Loading Spinner */
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : processed && processed.categoryList.length > 0 ? (
            /* 2. Card aur Table tabhi render hoga jab data mil jayega */
            <Card className="shadow-sm border-0">
              <Card.Body>
                {/* Student Details section */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 text-secondary fw-semibold">
                  <div>Name: <span className="text-dark">{student_details.student_full_name || 'N/A'}</span></div>
                  <div>Admission: <span className="text-dark">{student_details.admission_no || 'N/A'}</span></div>
                  <div>Class: <span className="text-dark">{student_details.class ? `${student_details.class} - ${student_details.section}` : 'N/A'}</span></div>
                  <div>Category: <span className="text-dark">{student_details.student_category || 'N/A'}</span></div>
                </div>

                {/* Responsive Matrix Table */}
                <div className="table-responsive">
                  <Table bordered hover size="sm" className="align-middle text-center mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="text-start ps-3">FEE CATEGORY</th>
                        {monthOrder.map(m => <th key={m.num}>{m.label}</th>)}
                        <th>AVE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Row 1: All Fees */}
                      <tr className="fw-semibold">
                        <td className="text-start ps-3">All Fees</td>
                        {monthOrder.map(m => (
                          <td key={m.num}>{processed.monthAllFees[m.num] ?? 0}</td>
                        ))}
                        <td>{overallAllFeesAvg}</td>
                      </tr>

                      {/* Dynamic Category Rows */}
                      {processed.categoryList.map(catName => {
                        let validMonthValues = [];
                        return (
                          <tr key={catName}>
                            <td className="text-start ps-3">{catName}</td>
                            {monthOrder.map(m => {
                              const val = processed.categoryMatrix[catName][m.num];
                              if (val !== undefined) validMonthValues.push(val);
                              return (
                                <td key={m.num}>
                                  {val !== undefined ? val : '-'}
                                </td>
                              );
                            })}
                            <td className="fw-semibold">
                              {validMonthValues.length > 0
                                ? (validMonthValues.reduce((a, b) => a + b, 0) / validMonthValues.length).toFixed(2)
                                : '-'}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Row Last: Average (excluding all fees) % */}
                      <tr className="fw-bold bg-light">
                        <td className="text-start ps-3">Average (excluding all fees) %</td>
                        {monthOrder.map(m => (
                          <td key={m.num}>
                            {(processed.monthAllFees[m.num] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td>{overallAllFeesAvg}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          ) : (
            /* 3. Data na hone par Card hidden rahega aur direct Message show hoga */
            <div className="text-center py-5 text-muted fw-bold fs-14">
              No Data Found
            </div>
          )}
        </Container>
      </div>
    </div>
  );
};

export default FeeAccuracyReport;